/**
 * Redux slice des alignements (chantier C23 « Confrontation », SP4, spec § 7.1) :
 * ce que je peux lire — mes alignements (`miens`) et ceux qui visent un de mes
 * concepts (`recus`) —, tels que le serveur les rend.
 *
 * Une lecture sans `entreeId` remplace tout ; une lecture d'un concept ne remplace
 * que ses lignes (les miennes depuis lui, les reçues vers lui). Poser insère ou
 * remplace dans `miens` ; retirer enlève. Les messages d'échec du serveur (400, 404)
 * remontent tels quels. Garde `requestId` sur la lecture (une réponse périmée est
 * ignorée) ; aucune notification globale.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { alignementService } from '../../services/alignementAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import type { AlignementDto, AlignementsPayload, PoseAlignement, PoseAlignementOrganisation } from '../../types/api';

export type AlignementsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AlignementsState {
    status: AlignementsStatus;
    miens: AlignementDto[];
    recus: AlignementDto[];
    requestId: string | null;
    erreur?: string;
}

export const initialAlignementsState: AlignementsState = { status: 'idle', miens: [], recus: [], requestId: null };

const NO_ALIGNEMENTS: AlignementDto[] = Object.freeze([]) as unknown as AlignementDto[];

async function tokenOf(instance: any): Promise<string> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    return accessToken;
}

/** Message d'un échec axios : celui du serveur (400, 404) sinon générique. */
function messageOf(error: any, fallback: string): string {
    return error?.response?.data?.message || error?.message || fallback;
}

export const fetchAlignements = createAsyncThunk<AlignementsPayload, { instance: any; entreeId?: string }, { rejectValue: string }>(
    'alignements/fetch',
    async ({ instance, entreeId }, { rejectWithValue }) => {
        try {
            return await alignementService.lire(await tokenOf(instance), entreeId);
        } catch (error) {
            return rejectWithValue(messageOf(error, 'Impossible de lire les correspondances'));
        }
    },
);

export const poserAlignement = createAsyncThunk<AlignementDto, { instance: any; pose: PoseAlignement | PoseAlignementOrganisation }, { rejectValue: string }>(
    'alignements/poser',
    async ({ instance, pose }, { rejectWithValue }) => {
        try {
            return await alignementService.poser(pose, await tokenOf(instance));
        } catch (error) {
            return rejectWithValue(messageOf(error, 'Enregistrement impossible. Réessayez.'));
        }
    },
);

export const retirerAlignement = createAsyncThunk<string, { instance: any; id: string }, { rejectValue: string }>(
    'alignements/retirer',
    async ({ instance, id }, { rejectWithValue }) => {
        try {
            await alignementService.retirer(id, await tokenOf(instance));
            return id;
        } catch (error) {
            return rejectWithValue(messageOf(error, 'Retrait impossible. Réessayez.'));
        }
    },
);

const alignementsSlice = createSlice({
    name: 'alignements',
    initialState: initialAlignementsState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAlignements.pending, (state, action) => {
                state.status = 'loading';
                state.requestId = action.meta.requestId;
                delete state.erreur;
            })
            .addCase(fetchAlignements.fulfilled, (state, action) => {
                if (state.requestId !== action.meta.requestId) return;
                const { entreeId } = action.meta.arg;
                if (entreeId) {
                    // Une lecture d'un concept ne remplace que ses lignes.
                    state.miens = [...state.miens.filter(a => a.source.entreeId !== entreeId), ...action.payload.miens];
                    state.recus = [...state.recus.filter(a => a.cible.entreeId !== entreeId), ...action.payload.recus];
                } else {
                    state.miens = action.payload.miens;
                    state.recus = action.payload.recus;
                }
                state.status = 'ready';
            })
            .addCase(fetchAlignements.rejected, (state, action) => {
                if (state.requestId !== action.meta.requestId) return;
                state.status = 'error';
                state.erreur = action.payload ?? 'Impossible de lire les correspondances';
            })
            .addCase(poserAlignement.fulfilled, (state, action) => {
                const a = action.payload;
                const index = state.miens.findIndex(m => m.id === a.id || (m.source.entreeId === a.source.entreeId && m.cible.entreeId === a.cible.entreeId));
                if (index >= 0) state.miens[index] = a; else state.miens.unshift(a);
            })
            .addCase(retirerAlignement.fulfilled, (state, action) => {
                state.miens = state.miens.filter(a => a.id !== action.payload);
            });
    },
});

export default alignementsSlice.reducer;

// --- Sélecteurs (tolérants : un store partiel rend des listes vides, stables) ---
export const selectAlignementsStatus = (state: any): AlignementsStatus => state?.alignements?.status ?? 'idle';
export const selectMesAlignements = (state: any): AlignementDto[] => state?.alignements?.miens ?? NO_ALIGNEMENTS;
export const selectAlignementsRecus = (state: any): AlignementDto[] => state?.alignements?.recus ?? NO_ALIGNEMENTS;
